import { getSessionAndCorrelationID } from "../util/getSessionCorrelationId";

/**
 * CommonFunctions class provides core text-to-speech functionality for blog content.
 * Implements singleton pattern and handles speech synthesis, voice selection, and playback control.
 * 
 * Features:
 * - Text extraction from blog content
 * - Speech synthesis with chunked text processing
 * - Voice selection with preference for natural female voices
 * - Playback duration calculation and formatting
 * - Play/pause/resume controls with accessibility support
 * - Cross-browser compatibility for speech synthesis API
 * - Adds a datalayer to track user behaviour via GTM tags
 * 
 * @class CommonFunctions
 * @since 1.0.0
 * @author Roshan Jose
 * @example
 * ```typescript
 * const commonFunctions = CommonFunctions.getInstance();
 * await commonFunctions.getVoices();
 * commonFunctions.speakText(playButton);
 * ```
 */
export class CommonFunctions {
    /** @private Static instance for singleton pattern */
    private static instance: CommonFunctions;
    
    /** @private Reference to the blog content container element */
    private blogContainer: HTMLElement | null = null;
    
    /** @private Extracted text content from the blog for speech synthesis */
    private blogText: string = "";
    
    /** @private Reference to the first image in blog content for poster display */
    private blogImage: HTMLImageElement | null = null;
    
    /** @private Array of available speech synthesis voices from the browser */
    private availableVoices: SpeechSynthesisVoice[] = [];
    
    /** @private Text split into manageable chunks for speech synthesis */
    private textChunks: string[] = [];
    
    /** @private Current position in the text chunks array during playback */
    private currentChunkIndex: number = 0;
    
    /** @public Current paused state of speech synthesis */
    isPaused: boolean = false;
    
    /** @public Current playing state of speech synthesis */
    isPlaying: boolean = false;

    /** @public Current page URL for analytics tracking */
    pageUrl: string = "";
    
    /** @private Speech synthesis configuration settings */
    private readonly speechSettingsUtterance: {
        /** Language code for speech synthesis */
        lang: string;
        /** Speech rate (0.1 to 10, default 1) */
        rate: number;
        /** Voice pitch (0 to 2, default 1) */
        pitch: number;
        /** Selected voice for speech synthesis */
        voice?: SpeechSynthesisVoice;
    };
    
    /** @private Browser detection for Firefox-specific workarounds */
    private readonly isFirefox: boolean = /Firefox/i.test(navigator.userAgent);

    /**
     * Gets the singleton instance of CommonFunctions.
     * Creates a new instance if one doesn't exist.
     * 
     * @static
     * @returns {CommonFunctions} The singleton instance
     * @example
     * ```typescript
     * const commonFunctions = CommonFunctions.getInstance();
     * ```
     */
    public static getInstance() {
        this.instance ??= new CommonFunctions();
        return this.instance;
    }
    
    /**
     * Private constructor for singleton pattern.
     * Initializes DOM lookup and speech synthesis settings.
     * 
     * @private
     * @constructor
     * @description
     * - Performs DOM lookup to find blog content elements
     * - Sets default speech synthesis settings:
     *   - Language: en-US
     *   - Rate: 1 (normal speed)
     *   - Pitch: 0.4 (slightly lower pitch for better readability)
     */
    constructor() {
        this.domLookup();
        this.pageUrl = window.location.href;
        this.speechSettingsUtterance = {
            lang: "en-US",
            rate: 1,
            pitch: 0.4
        };
    }

    /**
     * Performs DOM lookup to find and cache blog content elements.
     * Sets up the blog container for text-to-speech functionality.
     * 
     * @private
     * @returns {void}
     * @description
     * - Finds the main blog content container (.entry-content)
     * - Locates the first image in the blog content for poster display
     * - Sets the container position to relative for proper styling
     * - Extracts text content for speech synthesis
     * 
     * @example
     * ```typescript
     * // Called automatically during initialization
     * this.domLookup();
     * ```
     */
    domLookup() {
        this.blogContainer = document.querySelector(".entry-content");
        this.blogImage = document.querySelector(".entry-content img")as HTMLImageElement;
        if (this.blogContainer) {
            this.blogContainer.style.position = "relative";
        }
        this.extractTextFromDiv();
    }
    
    /**
     * Extracts text content from the blog container for speech synthesis.
     * Uses fallback methods to ensure text extraction works across browsers.
     * 
     * @private
     * @returns {string} The extracted text content
     * @description
     * Extraction priority:
     * 1. textContent (preferred - excludes HTML tags)
     * 2. innerText (fallback - respects styling visibility)
     * 3. Empty string if no container found
     * 
     * @example
     * ```typescript
     * const text = this.extractTextFromDiv();
     * console.log(`Extracted ${text.length} characters`);
     * ```
     */
    extractTextFromDiv() : string {
        if (!this.blogContainer) {
            return "";
        }
        this.blogText = this.blogContainer.textContent || this.blogContainer.innerText || "";
        return this.blogText;
    }

    /**
     * Calculates the estimated playback duration for the blog text.
     * Uses word count and speech rate to estimate reading time.
     * 
     * @public
     * @returns {number} Estimated duration in seconds
     * @description
     * Calculation method:
     * 1. Cleans text by normalizing whitespace
     * 2. Counts words (non-empty strings separated by whitespace)
     * 3. Uses base reading rate of 150 words per minute
     * 4. Adjusts for current speech rate setting
     * 5. Converts to seconds and rounds up
     * 
     * @example
     * ```typescript
     * const duration = commonFunctions.calculatePlaybackDuration();
     * console.log(`Estimated duration: ${duration} seconds`);
     * ```
     */
    calculatePlaybackDuration(): number {
        if (!this.blogText || this.blogText.trim().length === 0) {
            return 0;
        }
        const cleanText = this.blogText.trim().replace(/\s+/g, " ");
        const wordCount = cleanText
            .split(/\s+/)
            .filter((word) => word.length > 0).length;

        const baseWordsPerMinute = 150;
        const adjustedWordsPerMinute =
            baseWordsPerMinute * this.speechSettingsUtterance.rate;

        const durationInMinutes = wordCount / adjustedWordsPerMinute;
        return Math.ceil(durationInMinutes * 60);
    }

    /**
     * Formats duration in seconds to a human-readable string.
     * Provides concise time format for display purposes.
     * 
     * @public
     * @param {number} seconds - Duration in seconds to format
     * @returns {string} Formatted duration string
     * @description
     * Format rules:
     * - Less than 60 seconds: "30s"
     * - Exact minutes: "5m"
     * - Minutes with seconds: "5m 30s"
     * 
     * @example
     * ```typescript
     * console.log(formatDuration(45));   // "45s"
     * console.log(formatDuration(120));  // "2m"
     * console.log(formatDuration(150));  // "2m 30s"
     * ```
     */
    formatDuration(seconds: number): string {
        if (seconds < 60) {
            return `${seconds}s`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        if (remainingSeconds === 0) {
            return `${minutes}m`;
        }
        return `${minutes}m ${remainingSeconds}s`;
    }

    /**
     * Gets the estimated playback time as a formatted string.
     * Combines duration calculation and formatting for display.
     * 
     * @public
     * @returns {string} Formatted estimated playback time
     * @description
     * Convenience method that:
     * 1. Calculates playback duration based on text content
     * 2. Formats the duration for user display
     * 
     * @example
     * ```typescript
     * const timeString = commonFunctions.getEstimatedPlaybackTime();
     * console.log(`Estimated read time: ${timeString}`); // "Estimated read time: 3m 45s"
     * ```
     */
    getEstimatedPlaybackTime(): string {
        const duration = this.calculatePlaybackDuration();
        return this.formatDuration(duration);
    }

    /**
     * Retrieves the blog title from the page for display purposes.
     * Looks for the standard blog title element.
     * 
     * @public
     * @returns {string} The blog title text or empty string if not found
     * @description
     * - Searches for element with class "entry-title"
     * - Extracts text content using textContent property
     * - Returns empty string if title element not found
     * 
     * @example
     * ```typescript
     * const title = commonFunctions.getBlogTitle();
     * console.log(`Blog title: ${title}`);
     * ```
     */
    getBlogTitle(): string {
        const title = document.querySelector(".entry-title");
        if (title) {
            return title.textContent || "";
        }
        return "";
    }

    /**
     * Gets the source URL of the first image in the blog content.
     * Used for poster image display in the audio player.
     * 
     * @public
     * @returns {string} The image source URL or empty string if no image found
     * @description
     * - Returns the src attribute of the first image in blog content
     * - Image is cached during domLookup() initialization
     * - Returns empty string if no image is available
     * 
     * @example
     * ```typescript
     * const imageSrc = commonFunctions.getBlogImage();
     * if (imageSrc) {
     *   console.log(`Using poster image: ${imageSrc}`);
     * }
     * ```
     */
    getBlogImage(): string {
        if (this.blogImage) {
            return this.blogImage.src;
        }
        return "";
    }

    /**
     * Asynchronously loads and processes available speech synthesis voices.
     * Handles cross-browser compatibility for voice loading.
     * 
     * @public
     * @async
     * @returns {Promise<void>} Promise that resolves when voices are loaded
     * @description
     * Voice loading strategy:
     * 1. Checks if speechSynthesis API is available
     * 2. Tries immediate voice loading (works in Firefox/Safari)
     * 3. Falls back to event-based loading for Chrome/Chromium
     * 4. Uses 3-second timeout as final fallback
     * 5. Processes voices to select optimal voice for speech
     * 
     * Browser compatibility:
     * - Firefox/Safari: Voices available immediately
     * - Chrome/Chromium: Voices load asynchronously via 'voiceschanged' event
     * 
     * @example
     * ```typescript
     * await commonFunctions.getVoices();
     * console.log('Voices loaded and processed');
     * ```
     */
    async getVoices(): Promise<void> {
        if (!globalThis.speechSynthesis) return;
    
        return new Promise<void>((resolve) => {
            // Get voices immediately (works in Firefox and Safari)
            let voices = globalThis.speechSynthesis.getVoices();
            
            if (voices.length > 0) {
                this.processVoices(voices);
                resolve();
                return;
            }

            // Handle Chrome/Chromium browsers where voices load asynchronously
            const handleVoicesChanged = () => {
                voices = globalThis.speechSynthesis.getVoices();
                if (voices.length > 0) {
                    this.processVoices(voices);
                    globalThis.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
                    resolve();
                }
            };

            // Listen for voices to become available
            globalThis.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
            
            // Fallback timeout
            setTimeout(() => {
                globalThis.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
                this.processVoices(globalThis.speechSynthesis.getVoices());
                resolve();
            }, 3000);
        });
    }

    /**
     * Processes available voices and selects the optimal voice for speech synthesis.
     * Implements intelligent voice selection with preference hierarchy.
     * 
     * @private
     * @param {SpeechSynthesisVoice[]} voices - Array of available voices from the browser
     * @returns {void}
     * @description
     * Voice selection priority:
     * 1. **Female Natural English Voices**: Preferred for better user experience
     *    - Searches for voices with female names (Ava, Emma, Ana, Aria, Jenny, Michelle)
     *    - Must be English (en-US) and contain "Natural" in name
     * 2. **Natural English Voices**: Fallback for any natural-sounding voice
     *    - Any voice containing "Natural" and English language
     * 3. **English Voices**: Final fallback for basic English voices
     *    - Any voice with en-US language support
     * 
     * Security: Uses crypto.getRandomValues() for secure random selection
     * within each category to provide variety while maintaining quality.
     * 
     * @example
     * ```typescript
     * // Called internally by getVoices()
     * this.processVoices(availableVoices);
     * ```
     */
    private processVoices(voices: SpeechSynthesisVoice[]) {
        this.availableVoices = voices;

        const englishVoices = this.availableVoices.filter((voice) =>
            voice.lang.includes("en-US")
        );

        const naturalVoices = englishVoices.filter((voice) =>
            voice.name.includes("Natural")
        );

        // Heuristic: detect female voices by name
        const femaleKeywords = ["Ava", "Emma", "Ana", "Aria", "Jenny", "Michelle"];
        const femaleVoices = naturalVoices.filter((voice) =>
            femaleKeywords.some((kw) => voice.name.includes(kw))
        );

        if(femaleVoices.length > 0){
            const randomIndex = self.crypto.getRandomValues(new Uint32Array(1))[0] % femaleVoices.length;
            this.speechSettingsUtterance.voice = femaleVoices[randomIndex];
        }else if(naturalVoices.length > 0){
            const randomIndex = self.crypto.getRandomValues(new Uint32Array(1))[0] % naturalVoices.length;
            this.speechSettingsUtterance.voice = naturalVoices[randomIndex];
        }else if(englishVoices.length > 0){
            const randomIndex = self.crypto.getRandomValues(new Uint32Array(1))[0] % englishVoices.length;
            this.speechSettingsUtterance.voice = englishVoices[randomIndex];
        }
    }

    /**
     * Initiates text-to-speech playback for the blog content.
     * Handles text chunking and starts the speech synthesis process.
     * 
     * @public
     * @param {HTMLButtonElement} playButton - The play button element for accessibility updates
     * @returns {void}
     * @description
     * Speech initiation process:
     * 1. Validates speechSynthesis API availability
     * 2. Checks for valid text content
     * 3. Cancels any existing speech synthesis
     * 4. Chunks text into manageable pieces (30 words each)
     * 5. Resets chunk index and begins playback
     * 
     * Error handling:
     * - Logs error if speechSynthesis API not supported
     * - Warns if no text content available
     * - Catches and logs any synthesis errors
     * 
     * @example
     * ```typescript
     * const playButton = document.getElementById('play-btn') as HTMLButtonElement;
     * commonFunctions.speakText(playButton);
     * ```
     */
    speakText(playButton: HTMLButtonElement) {
        if (!("speechSynthesis" in globalThis)) {
            console.error("SpeechSynthesis API is not supported in this browser.");
            return;
        }

        const text = this.blogText;
        if (!text.trim()) {
            console.warn("No text provided for speech.");
            return;
        }
        try {
            globalThis.speechSynthesis.cancel();
            this.chunkText(text);
            this.currentChunkIndex = 0; 
            this.playChunk(playButton);
        } catch (error) {
            console.error("Error during speech synthesis:", error);
        }
    }

    /**
     * Splits text into smaller chunks for reliable speech synthesis.
     * Prevents issues with long text that might cause synthesis failures.
     * 
     * @private
     * @param {string} text - The text to be chunked
     * @returns {void}
     * @description
     * Chunking strategy:
     * - Splits text by whitespace to get individual words
     * - Groups words into chunks of 30 words each
     * - Stores chunks in textChunks array for sequential playback
     * - Preserves word boundaries and spacing
     * 
     * Why chunking is necessary:
     * - Some browsers have limits on utterance length
     * - Long text can cause synthesis to fail or timeout
     * - Chunking provides better control over playback
     * - Enables pause/resume functionality between chunks
     * 
     * @example
     * ```typescript
     * // Called internally by speakText()
     * this.chunkText("This is a long blog post that needs to be split...");
     * ```
     */
    chunkText(text: string) {
        const words = text.split(/\s+/);
        const chunkSize = 30;
        this.textChunks = [];
        for (let i = 0; i < words.length; i += chunkSize) {
            this.textChunks.push(words.slice(i, i + chunkSize).join(" "));
        }
    }

    /**
     * Plays a single text chunk using speech synthesis.
     * Handles utterance configuration and event management.
     * 
     * @private
     * @param {HTMLButtonElement} playButton - The play button for accessibility updates
     * @returns {void}
     * @description
     * Chunk playback process:
     * 1. Checks if all chunks have been played (stops if complete)
     * 2. Creates SpeechSynthesisUtterance for current chunk
     * 3. Applies speech settings (language, rate, pitch, voice)
     * 4. Sets up event handlers for start, end, and error events
     * 5. Initiates speech synthesis
     * 
     * Event handling:
     * - **onstart**: Updates playing state and button accessibility
     * - **onend**: Advances to next chunk and continues playback
     * - **onerror**: Logs error and resets button accessibility
     * 
     * Accessibility:
     * - Updates aria-label for screen reader users
     * - Provides appropriate button state descriptions
     * 
     * @example
     * ```typescript
     * // Called internally by speakText() and recursively for each chunk
     * this.playChunk(playButton);
     * ```
     */
    playChunk(playButton: HTMLButtonElement) {
        if (this.currentChunkIndex >= this.textChunks.length) {
            this.isPlaying = false;
            return;
        }

        const chunk = this.textChunks[this.currentChunkIndex];
        const utterance = new SpeechSynthesisUtterance(chunk);
        utterance.lang = this.speechSettingsUtterance.lang;
        utterance.rate = this.speechSettingsUtterance.rate;
        utterance.pitch = this.speechSettingsUtterance.pitch;
        
        
        if (this.speechSettingsUtterance.voice) {
            utterance.voice = this.speechSettingsUtterance.voice;
        }

        utterance.onstart = () => {
            this.isPlaying = true;
            if (playButton) {
                playButton.setAttribute("aria-label", "Pause blog article audio");
            }
        };

        utterance.onend = () => {
            // Firefox fix: Check if we're still supposed to be playing
            if (!this.isPlaying || this.isPaused) {
                return; // Don't continue if paused/stopped
            }
            this.currentChunkIndex++;
            this.playChunk(playButton);
        };

        utterance.onerror = (event) => {
            console.error("Speech synthesis error:", event.error);
            if (playButton) {
                playButton.setAttribute("aria-label", "Play blog article audio");
            }
        };

        globalThis.speechSynthesis.speak(utterance);
    }

    /**
     * Pauses the current speech synthesis playback.
     * Cancels current utterance and sets paused state.
     * 
     * @public
     * @param {HTMLButtonElement} playButton - The play button for accessibility updates
     * @returns {void}
     * @description
     * Pause behavior:
     * 1. Checks if speech is currently playing (returns early if not)
     * 2. Cancels current speech synthesis utterance
     * 3. Updates internal state flags (isPlaying = false, isPaused = true)
     * 4. Updates button accessibility for screen readers
     * 
     * Note: Uses cancel() instead of pause() because:
     * - pause() has inconsistent browser support
     * - cancel() is more reliable across browsers
     * - Resume functionality reconstructs from current chunk position
     * 
     * @example
     * ```typescript
     * const playButton = document.getElementById('play-btn') as HTMLButtonElement;
     * commonFunctions.pauseSpeech(playButton);
     * ```
     */
    pauseSpeech(playButton: HTMLButtonElement) {
        if (!this.isPlaying) return;
        
        // Set paused state BEFORE canceling to prevent race conditions
        this.isPlaying = false;
        this.isPaused = true;
        try{
            // Cancel speech synthesis with Firefox-specific handling
            globalThis.speechSynthesis.cancel();
            
            // Firefox workaround: Additional measures for reliable stopping
            if (this.isFirefox) {
                // Firefox sometimes needs multiple cancel calls
                setTimeout(() => {
                    if (globalThis.speechSynthesis.speaking) {
                        globalThis.speechSynthesis.cancel();
                    }
                }, 50);
                
                // Final fallback for stubborn Firefox instances
                setTimeout(() => {
                    if (globalThis.speechSynthesis.speaking) {
                        globalThis.speechSynthesis.cancel();
                        console.warn('Firefox required multiple cancel attempts');
                    }
                }, 200);
            }
            
            if (playButton) {
                playButton.setAttribute("aria-label", "Resume blog article audio");
            }
        }
        catch (error) {
            console.warn("Error pausing speech:", error);
        }
    }

    /**
     * Resumes speech synthesis from the current chunk position.
     * Continues playback from where it was paused.
     * 
     * @public
     * @param {HTMLButtonElement} playButton - The play button for accessibility updates
     * @returns {void}
     * @description
     * Resume behavior:
     * 1. Checks if speech is currently paused (returns early if not)
     * 2. Resumes playback from current chunk index position
     * 3. Updates internal state flags (isPaused = false, isPlaying = true)
     * 4. Updates button accessibility for screen readers
     * 
     * Resume strategy:
     * - Maintains current chunk index position
     * - Restarts from beginning of current chunk (not mid-chunk)
     * - Provides seamless continuation of speech synthesis
     * 
     * @example
     * ```typescript
     * const playButton = document.getElementById('play-btn') as HTMLButtonElement;
     * commonFunctions.resumeSpeech(playButton);
     * ```
     */
    resumeSpeech(playButton: HTMLButtonElement) {
        if (!this.isPaused) return;
        this.playChunk(playButton);
        this.isPaused = false;
        this.isPlaying = true;
        if (playButton) {
            playButton.setAttribute("aria-label", "Pause blog article audio");
        }
    }

    /**
     * Sends analytics events to the dataLayer for tracking text-to-speech interactions.
     * Captures user engagement metrics with audio playback for analytics platforms.
     * 
     * @public
     * @param {string} eventType - Type of audio event ('play', 'pause', 'end', 'ignored')
     * @param {string} audioTitle - Title of the audio content being played
     * @param {number} audioDuration - Total duration of the audio in seconds
     * @param {number} [elapsedTime] - Optional elapsed playback time in seconds (for pause/end events)
     * @returns {void}
     * 
     * @description
     * Event Types and Their Meanings:
     * - **'play'**: User initiated audio playback → Triggers 'audio_played' event
     * - **'pause'**: User paused the audio → Triggers 'audio_paused' event with duration
     * - **'end'**: Audio playback completed → Triggers 'audio_ended' event with duration
     * - **'ignored'**: User closed audio without playing → Triggers 'audio_ignored' event
     * - **default**: Unknown event types default to 'audio_played'
     * 
     * DataLayer Event Structure:
     * - page_location: Current page URL
     * - audio_title: Title of the audio content
     * - audio_total_time: Total audio duration in seconds
     * - session_data: Session ID from correlation tracking
     * - session_timeStamp: ISO 8601 formatted timestamp (human-readable)
     * - event: Analytics event name
     * - audio_played_duration: Elapsed time (only for pause/end events)
     * 
     * Security Features:
     * - Handles null/undefined values gracefully with fallback to empty string/0
     * - Session data retrieved securely from correlation ID system
     * - Timestamp in ISO format for better readability and timezone handling
     * 
     * @example
     * ```typescript
     * // Track audio play event
     * commonFunctions.analyticsEvent('play', 'Blog Article Audio', 180);
     * 
     * // Track audio pause with elapsed time
     * commonFunctions.analyticsEvent('pause', 'Blog Article Audio', 180, 45);
     * 
     * // Track audio completion
     * commonFunctions.analyticsEvent('end', 'Blog Article Audio', 180, 180);
     * 
     * // Track ignored audio (closed without playing)
     * commonFunctions.analyticsEvent('ignored', 'Blog Article Audio', 180);
     * ```
     * 
     * @throws {Error} Throws if window.dataLayer is not defined
     * 
     * @see {@link getSessionAndCorrelationID} For session tracking implementation
     */
    analyticsEvent(eventType: string, audioTitle: string, audioDuration: number, elapsedTime?: number) {
        let dataLayer = (window as any).dataLayer;
        const sessionAndCorelation = getSessionAndCorrelationID()
        const dataLayerEvent: {
            page_location: string;
            audio_title: string;
            audio_total_time: number;
            event: string;
            session_data: string;
            session_timeStamp: string;
            audio_played_duration?: number;
        } = {
            page_location: this.pageUrl,
            audio_title: audioTitle ?? "",
            audio_total_time: (audioDuration ?? 0) as number,
            session_data : sessionAndCorelation.sessionID ?? "",
            session_timeStamp: new Date().toISOString() ?? "",
            event: "",
        };
        switch (eventType) {
            case 'play':
                dataLayerEvent.event = 'audio_played';
                break;
            case 'pause':
                dataLayerEvent.event = 'audio_paused';
                dataLayerEvent.audio_played_duration = elapsedTime;
                break;
            case 'end':
                dataLayerEvent.event = 'audio_ended';
                dataLayerEvent.audio_played_duration = elapsedTime;
                break;
            case 'ignored':
                dataLayerEvent.event = 'audio_ignored';
                dataLayerEvent.audio_played_duration = 0;
                break;
            default:
                dataLayerEvent.event = 'audio_played';
                break;
        }
        dataLayer.push(dataLayerEvent);
    }
}
